import { type MouseEvent, useEffect, useMemo, useRef } from 'react';
import {
  components,
  type MultiValue,
  type MultiValueRemoveProps,
} from 'react-select';

import { usePrimaryFilterState } from '../../../filters/hooks/usePrimaryFilterState';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { useTabContentConfig } from '../../PageConfigProvider/PageConfigProvider';
import type { PrimaryEntityOption } from '../../PageConfigProvider/types/chart';
import { usePageState } from '../../PageState/PageStateProvider';
import { isMultiEntitySwitcherChart } from '../../PageState/utils/is-multi-entity-switcher-chart';
import { useChart } from '../ChartProvider';
import { StyledPlotSelect } from '../StyledPlotSelect';
import styles from './chart-switcher.module.css';

// Custom MultiValueRemove component that hides the clear button when only one item is selected
const CustomMultiValueRemove = (
  props: MultiValueRemoveProps<PrimaryEntityOption, true>
) => {
  const { selectProps } = props;
  const selectedOptions = selectProps.value as PrimaryEntityOption[];

  // Hide the remove button if there's only one selected item
  if (selectedOptions.length <= 1) {
    return null;
  }

  return <components.MultiValueRemove {...props} />;
};

export const MultiPrimaryEntitySwitcher = ({
  primaryFilterId,
}: {
  primaryFilterId: PrimaryFilterId;
}) => {
  const { item } = useChart();
  const { revelioPageId } = useTabContentConfig();
  const { pageState, dispatch: pageDispatch } = usePageState();

  const { primaryFilter: primaryFilterState } =
    usePrimaryFilterState(primaryFilterId);

  const primaryEntityOptions: PrimaryEntityOption[] = useMemo(() => {
    return (
      primaryFilterState?.primarySelections?.map((selection) => ({
        label: selection?.name,
        value: {
          id: selection?.id.split('_').pop() || selection?.id,
          name: selection?.name,
          levelId: selection.levelId,
        },
      })) || []
    );
  }, [primaryFilterState?.primarySelections]);

  const selectedMultiPrimaryEntityOptions = (() => {
    const selectedEntities = pageState.multiPrimaryEntities?.[item?.id];
    if (!selectedEntities || selectedEntities.length === 0) return [];

    return primaryEntityOptions.filter((option) =>
      selectedEntities.some((entity) => entity.id === option.value.id)
    );
  })();

  const showSwitcher = isMultiEntitySwitcherChart(item) && !revelioPageId;

  // Sync selection whenever the list of available primary entities changes.
  const lastOptionSignatureRef = useRef<string>('');

  useEffect(() => {
    if (!showSwitcher) return;

    const optionValues = primaryEntityOptions.map((o) => o.value);
    const optionSignature = optionValues.map((v) => v.id).join(',');

    if (optionSignature !== lastOptionSignatureRef.current) {
      lastOptionSignatureRef.current = optionSignature;
      pageDispatch({
        type: 'SET_MULTI_PRIMARY_ENTITIES',
        payload: { plotId: item?.id, primaryEntities: optionValues },
      });
    }
  }, [showSwitcher, item?.id, pageDispatch, primaryEntityOptions]);

  const handleSelectedMultiPrimaryFilterChange = (
    entities: MultiValue<PrimaryEntityOption>
  ) => {
    pageDispatch({
      type: 'SET_MULTI_PRIMARY_ENTITIES',
      payload: {
        plotId: item?.id,
        primaryEntities: entities.map((entity) => entity.value),
        fromChartSwitcher: true,
      },
    });
  };

  if (!showSwitcher || primaryEntityOptions.length <= 1) {
    return null;
  }

  const placeholderLabel = (() => {
    const values = selectedMultiPrimaryEntityOptions;
    if (!values || values.length === 0) return 'Select entities...';
    return `${values[0]?.label}${values.length > 1 ? ` + ${values.length - 1} others` : ''}`;
  })();

  return (
    <div
      className={styles.switcherBox}
      onClick={(e: MouseEvent) => e.stopPropagation()}
      data-testid="multi-primary-entity-switcher"
    >
      <StyledPlotSelect<PrimaryEntityOption, true>
        value={selectedMultiPrimaryEntityOptions}
        onChange={handleSelectedMultiPrimaryFilterChange}
        options={primaryEntityOptions}
        height="24px"
        classNamePrefix="multi-primary-switch"
        isMulti={true}
        isClearable={false}
        closeMenuOnSelect={false}
        components={{ MultiValueRemove: CustomMultiValueRemove }}
        controlShouldRenderValue={false}
        hideSelectedOptions={false}
        isOptionSelected={(option) =>
          selectedMultiPrimaryEntityOptions.some(
            (selected) => selected.value.id === option.value.id
          )
        }
        isOptionDisabled={(option) => {
          const isSelected = selectedMultiPrimaryEntityOptions.some(
            (selected) => selected.value.id === option.value.id
          );
          const selectedCount = selectedMultiPrimaryEntityOptions.length;
          // Prevent deselecting the last remaining selection via the menu
          return isSelected && selectedCount <= 1;
        }}
        placeholder={placeholderLabel}
      />
    </div>
  );
};
